/**
 * Books — the accountant's view.
 *
 * Daily sales, tax split, returns, purchases, stock, and waste, plus an
 * Excel pack whose columns match the documents an ArmSoft accountant posts.
 * Not a general ledger.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { supabase } from '../supabase.js';
import { resolveAppLocale } from '../locale.js';
import {
  downloadBooksPack,
  EMPTY_CODES,
  orderKindLabel,
  stayOptionLabel,
  stayStatusLabel,
  stayTenderLabel,
  taxTypeLabel,
  type ArmsoftCodes,
  type BooksPack,
} from '../lib/booksExport.js';
import {
  Button,
  Callout,
  DateRange,
  Field,
  FormGrid,
  Modal,
  PageHeader,
  Stat,
  Stats,
} from '../ui/index.js';
import type { DateRangePreset, DateRangeValue } from '../ui/index.js';
import { resolveDateRange } from '../ui/index.js';

const PRESETS: readonly DateRangePreset[] = [
  'today',
  '7d',
  '30d',
  'this_month',
  'last_month',
  'custom',
];

type Tab =
  | 'sales'
  | 'stays'
  | 'orders'
  | 'onHand'
  | 'tax'
  | 'returns'
  | 'comps'
  | 'voids'
  | 'b2b'
  | 'purchases'
  | 'stock'
  | 'waste'
  | 'drops'
  | 'expenses';

type TabItem = { value: Tab; label: string; count: number };

function num(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function asPack(raw: unknown): BooksPack {
  const o = (raw ?? {}) as Record<string, unknown>;
  const list = <T,>(key: string) => (Array.isArray(o[key]) ? (o[key] as T[]) : []);
  return {
    sales: list<BooksPack['sales'][number]>('sales').map((row) => ({
      ...row,
      orders: num(row.orders),
      gross: num(row.gross),
      cash: num(row.cash),
      card: num(row.card),
      tips: num(row.tips),
      fiscal_orders: num(row.fiscal_orders),
      missing_fiscal: num(row.missing_fiscal),
      missing_fiscal_amount: num(row.missing_fiscal_amount),
    })),
    tax: list<BooksPack['tax'][number]>('tax').map((r) => ({
      ...r,
      department_id: r.department_id == null ? null : num(r.department_id),
      department_name: r.department_name ?? null,
      tax_type: num(r.tax_type),
      net: num(r.net),
      discounts: num(r.discounts),
      cogs: num(r.cogs),
    })),
    orderLines: list<BooksPack['orderLines'][number]>('orderLines').map((r) => ({
      ...r,
      order_number: num(r.order_number),
      department_id: r.department_id == null ? null : num(r.department_id),
      department_name: r.department_name ?? null,
      tax_type: num(r.tax_type),
      line_net: num(r.line_net),
      discount: num(r.discount),
      cash: num(r.cash),
      card: num(r.card),
      tip: num(r.tip),
    })),
    onHand: list<BooksPack['onHand'][number]>('onHand').map((r) => ({
      ...r,
      quantity: num(r.quantity),
      unit_cost: num(r.unit_cost),
      value: num(r.value),
    })),
    stays: list<BooksPack['stays'][number]>('stays').map((r) => ({
      ...r,
      nights: num(r.nights),
      amount: num(r.amount),
    })),
    returns: list<BooksPack['returns'][number]>('returns').map((r) => ({
      ...r,
      total: num(r.total),
    })),
    comps: list<BooksPack['comps'][number]>('comps').map((r) => ({
      ...r,
      count: num(r.count),
      amount: num(r.amount),
    })),
    voids: list<BooksPack['voids'][number]>('voids').map((r) => ({ ...r, total: num(r.total) })),
    b2b: list<BooksPack['b2b'][number]>('b2b').map((r) => ({ ...r, total: num(r.total) })),
    purchases: list<BooksPack['purchases'][number]>('purchases').map((r) => ({
      ...r,
      quantity: num(r.quantity),
      unit_price: num(r.unit_price),
      line_total: num(r.line_total),
      invoice_vat: num(r.invoice_vat),
      invoice_total: num(r.invoice_total),
    })),
    stock: list<BooksPack['stock'][number]>('stock').map((r) => ({
      ...r,
      system_qty: num(r.system_qty),
      counted_qty: num(r.counted_qty),
      delta: num(r.delta),
      unit_cost: num(r.unit_cost),
      value: num(r.value),
    })),
    waste: list<BooksPack['waste'][number]>('waste').map((r) => ({
      ...r,
      quantity: num(r.quantity),
      unit_cost: num(r.unit_cost),
      value: num(r.value),
    })),
    drops: list<BooksPack['drops'][number]>('drops').map((r) => ({
      ...r,
      expected_amount: num(r.expected_amount),
      counted_amount: r.counted_amount == null ? null : num(r.counted_amount),
    })),
    expenses: list<BooksPack['expenses'][number]>('expenses').map((r) => ({
      ...r,
      amount: num(r.amount),
    })),
  };
}

export function BooksView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const money = useCallback(
    (n: number) =>
      `${Math.round(n).toLocaleString(locale)} ֏`,
    [locale],
  );
  const [range, setRange] = useState<DateRangeValue>({ preset: 'this_month' });
  const [pack, setPack] = useState<BooksPack | null>(null);
  const [codes, setCodes] = useState<ArmsoftCodes>(EMPTY_CODES);
  const [draft, setDraft] = useState<ArmsoftCodes>(EMPTY_CODES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codesOpen, setCodesOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('sales');

  const bounds = resolveDateRange(range);

  const load = useCallback(async () => {
    if (!supabase || !clientId || !bounds.from || !bounds.to) return;
    setLoading(true);
    setError(null);
    const db = supabase as unknown as {
      rpc: (
        fn: string,
        args: Record<string, string>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
      from: (table: string) => {
        select: (cols: string) => {
          eq: (
            col: string,
            val: string,
          ) => {
            maybeSingle: () => Promise<{
              data: ArmsoftCodes | null;
              error: { message: string } | null;
            }>;
          };
        };
        upsert: (row: ArmsoftCodes & { client_id: string }) => Promise<{ error: { message: string } | null }>;
      };
    };
    const [booksRes, codesRes] = await Promise.all([
      db.rpc('accountant_books', { p_from: bounds.from, p_to: bounds.to }),
      db
        .from('armsoft_export_settings')
        .select(
          'cash_desk_code, card_account_code, vat_income_code, exempt_income_code, turnover_income_code, purchase_account_code',
        )
        .eq('client_id', clientId)
        .maybeSingle(),
    ]);
    setLoading(false);
    if (booksRes.error) {
      setError(booksRes.error.message);
      setPack(null);
      return;
    }
    setPack(asPack(booksRes.data));
    if (!codesRes.error && codesRes.data) {
      const next: ArmsoftCodes = {
        cash_desk_code: codesRes.data.cash_desk_code ?? '',
        card_account_code: codesRes.data.card_account_code ?? '',
        vat_income_code: codesRes.data.vat_income_code ?? '',
        exempt_income_code: codesRes.data.exempt_income_code ?? '',
        turnover_income_code: codesRes.data.turnover_income_code ?? '',
        purchase_account_code: codesRes.data.purchase_account_code ?? '',
      };
      setCodes(next);
      setDraft(next);
    }
  }, [bounds.from, bounds.to, clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(() => {
    const sales = pack?.sales ?? [];
    return {
      gross: sales.reduce((s, r) => s + r.gross, 0),
      cash: sales.reduce((s, r) => s + r.cash, 0),
      card: sales.reduce((s, r) => s + r.card, 0),
      missing: sales.reduce((s, r) => s + r.missing_fiscal, 0),
      missingAmount: sales.reduce((s, r) => s + r.missing_fiscal_amount, 0),
    };
  }, [pack]);

  async function saveCodes() {
    if (!supabase || !clientId) return;
    setSaving(true);
    setSaved(null);
    const db = supabase as unknown as {
      from: (table: string) => {
        upsert: (row: ArmsoftCodes & { client_id: string }) => Promise<{ error: { message: string } | null }>;
      };
    };
    const { error: err } = await db.from('armsoft_export_settings').upsert({
      client_id: clientId,
      ...draft,
    });
    setSaving(false);
    if (err) {
      setSaved(t('books.saveFailed'));
      return;
    }
    setCodes(draft);
    setSaved(t('books.saved'));
    setCodesOpen(false);
  }

  function download() {
    if (!pack || !bounds.from || !bounds.to) return;
    downloadBooksPack(pack, codes, t, `orio-books-${bounds.from}-${bounds.to}.xlsx`);
  }

  const tabs: TabItem[] = pack
    ? [
        { value: 'sales', label: t('books.sheetSales'), count: pack.sales.length },
        { value: 'stays', label: t('books.stays'), count: pack.stays.length },
        { value: 'orders', label: t('books.sheetOrders'), count: pack.orderLines.length },
        { value: 'tax', label: t('books.tax'), count: pack.tax.length },
        { value: 'returns', label: t('books.returns'), count: pack.returns.length },
        { value: 'comps', label: t('books.comps'), count: pack.comps.length },
        { value: 'voids', label: t('books.voids'), count: pack.voids.length },
        { value: 'b2b', label: t('books.b2b'), count: pack.b2b.length },
        { value: 'purchases', label: t('books.purchases'), count: pack.purchases.length },
        { value: 'onHand', label: t('books.onHand'), count: pack.onHand.length },
        { value: 'stock', label: t('books.stock'), count: pack.stock.length },
        { value: 'waste', label: t('books.waste'), count: pack.waste.length },
        { value: 'drops', label: t('books.drops'), count: pack.drops.length },
        { value: 'expenses', label: t('books.expenses'), count: pack.expenses.length },
      ]
    : [];

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('books.title')}
        subtitle={t('books.subtitle')}
        actions={
          <>
            <Button variant="secondary" onClick={() => setCodesOpen(true)}>
              {t('books.codes')}
            </Button>
            <Button
              variant="primary"
              icon={<Download />}
              disabled={!pack || loading}
              onClick={download}
            >
              {t('books.download')}
            </Button>
          </>
        }
      />
      <DateRange value={range} onChange={setRange} presets={PRESETS} alwaysDates />

      {error && (
        <Callout tone="danger" title={t('books.loadFailed')}>
          {error}
        </Callout>
      )}

      {!error && pack && totals.missing > 0 && (
        <Callout tone="warning" title={t('books.missingFiscal')}>
          {t('books.fiscalGap', {
            count: totals.missing,
            amount: money(totals.missingAmount),
          })}
        </Callout>
      )}

      {saved && !codesOpen && <p className="muted">{saved}</p>}

      <Stats>
        <Stat label={t('books.paid')} value={money(totals.gross)} />
        <Stat label={t('books.cash')} value={money(totals.cash)} />
        <Stat label={t('books.card')} value={money(totals.card)} />
        <Stat label={t('books.missingFiscal')} value={String(totals.missing)} />
      </Stats>

      {!error && (
        <div className="panel books-detail">
          {loading ? (
            <div className="empty-state">{t('common.loading')}</div>
          ) : pack ? (
            <>
              <div className="books-tabs" role="tablist">
                {tabs.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="tab"
                    aria-selected={item.value === tab}
                    onClick={() => setTab(item.value)}
                    ref={(el) => {
                      if (el && item.value === tab) {
                        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                      }
                    }}
                  >
                    {item.label}
                    <span className="books-tabs-count">{item.count}</span>
                  </button>
                ))}
              </div>
              <DetailTable
                tab={tab}
                pack={pack}
                money={money}
                taxLabel={(n) => taxTypeLabel(t, n)}
              />
            </>
          ) : (
            <div className="empty-state">{t('books.empty')}</div>
          )}
        </div>
      )}

      <Modal
        open={codesOpen}
        onClose={() => setCodesOpen(false)}
        title={t('books.codesTitle')}
        caption={t('books.codesHint')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCodesOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={saving} onClick={() => void saveCodes()}>
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </>
        }
      >
        <FormGrid>
          <CodeField
            id="cash-desk"
            label={t('books.cashDesk')}
            value={draft.cash_desk_code}
            onChange={(v) => setDraft({ ...draft, cash_desk_code: v })}
          />
          <CodeField
            id="card-account"
            label={t('books.cardAccount')}
            value={draft.card_account_code}
            onChange={(v) => setDraft({ ...draft, card_account_code: v })}
          />
          <CodeField
            id="vat-income"
            label={t('books.vatIncome')}
            value={draft.vat_income_code}
            onChange={(v) => setDraft({ ...draft, vat_income_code: v })}
          />
          <CodeField
            id="exempt-income"
            label={t('books.exemptIncome')}
            value={draft.exempt_income_code}
            onChange={(v) => setDraft({ ...draft, exempt_income_code: v })}
          />
          <CodeField
            id="turnover-income"
            label={t('books.turnoverIncome')}
            value={draft.turnover_income_code}
            onChange={(v) => setDraft({ ...draft, turnover_income_code: v })}
          />
          <CodeField
            id="purchase-account"
            label={t('books.purchaseAccount')}
            value={draft.purchase_account_code}
            onChange={(v) => setDraft({ ...draft, purchase_account_code: v })}
          />
        </FormGrid>
      </Modal>
    </section>
  );
}

function CodeField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} optional htmlFor={id}>
      <input
        id={id}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

function DetailTable({
  tab,
  pack,
  money,
  taxLabel,
}: {
  tab: Tab;
  pack: BooksPack;
  money: (n: number) => string;
  taxLabel: (n: number) => string;
}) {
  const { t } = useTranslation();
  const empty = <div className="empty-state">{t('books.empty')}</div>;

  if (tab === 'sales') {
    if (pack.sales.length === 0) return empty;
    return (
      <SimpleTable
        headers={[
          t('books.day'),
          t('books.orders'),
          t('books.gross'),
          t('books.cash'),
          t('books.card'),
          t('books.tips'),
          t('books.fiscalReceipts'),
          t('books.missingFiscal'),
        ]}
        rows={pack.sales.map((r) => [
          r.day,
          String(r.orders),
          money(r.gross),
          money(r.cash),
          money(r.card),
          money(r.tips),
          String(r.fiscal_orders),
          String(r.missing_fiscal),
        ])}
      />
    );
  }
  if (tab === 'stays') {
    if (pack.stays.length === 0) return empty;
    return (
      <SimpleTable
        headers={[
          t('books.checkIn'),
          t('books.checkOut'),
          t('books.room'),
          t('books.guest'),
          t('books.nights'),
          t('books.amount'),
          t('books.paymentOption'),
          t('books.paymentStatus'),
          t('books.tender'),
          t('books.fiscalNo'),
        ]}
        rows={pack.stays.map((r) => [
          r.check_in,
          r.check_out,
          r.room,
          r.guest_name,
          String(r.nights),
          money(r.amount),
          stayOptionLabel(t, r.payment_option),
          stayStatusLabel(t, r.payment_status),
          stayTenderLabel(t, r.payment_method) || '—',
          r.fiscal_no ?? '—',
        ])}
      />
    );
  }
  if (tab === 'orders') {
    if (pack.orderLines.length === 0) return empty;
    return (
      <SimpleTable
        headers={[
          t('books.day'),
          t('books.order'),
          t('books.kind'),
          t('books.departmentNo'),
          t('books.lineTotal'),
          t('books.orderCash'),
          t('books.orderCard'),
          t('books.fiscalNo'),
        ]}
        rows={pack.orderLines.map((r) => [
          r.day,
          String(r.order_number),
          orderKindLabel(t, r.kind),
          r.department_id == null ? '' : String(r.department_id),
          money(r.line_net),
          money(r.cash),
          money(r.card),
          r.fiscal_no ?? '',
        ])}
      />
    );
  }
  if (tab === 'onHand') {
    if (pack.onHand.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.ingredient'), t('books.unit'), t('books.qty'), t('books.unitPrice'), t('books.value')]}
        rows={pack.onHand.map((r) => [
          r.ingredient,
          r.unit,
          String(r.quantity),
          money(r.unit_cost),
          money(r.value),
        ])}
      />
    );
  }
  if (tab === 'tax') {
    if (pack.tax.length === 0) return empty;
    return (
      <SimpleTable
        headers={[
          t('books.day'),
          t('books.departmentNo'),
          t('books.departmentName'),
          t('books.taxType'),
          t('books.net'),
          t('books.discounts'),
        ]}
        rows={pack.tax.map((r) => [
          r.day,
          r.department_id == null ? '' : String(r.department_id),
          r.department_name ?? '',
          taxLabel(r.tax_type),
          money(r.net),
          money(r.discounts),
        ])}
      />
    );
  }
  if (tab === 'returns') {
    if (pack.returns.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.order'), t('books.amount'), t('books.fiscalNo'), t('books.tin')]}
        rows={pack.returns.map((r) => [r.day, String(r.order_number), money(r.total), r.fiscal_no ?? '', r.partner_tin ?? ''])}
      />
    );
  }
  if (tab === 'comps') {
    if (pack.comps.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.compType'), t('books.count'), t('books.amount')]}
        rows={pack.comps.map((r) => [r.day, r.comp_type, String(r.count), money(r.amount)])}
      />
    );
  }
  if (tab === 'voids') {
    if (pack.voids.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.order'), t('books.amount'), t('books.closeReason'), t('books.compType')]}
        rows={pack.voids.map((r) => [
          r.day,
          String(r.order_number),
          money(r.total),
          r.close_reason ?? '',
          r.comp_type ?? '',
        ])}
      />
    );
  }
  if (tab === 'b2b') {
    if (pack.b2b.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.order'), t('books.tin'), t('books.amount'), t('books.fiscalNo')]}
        rows={pack.b2b.map((r) => [r.day, String(r.order_number), r.tin, money(r.total), r.fiscal_no ?? ''])}
      />
    );
  }
  if (tab === 'purchases') {
    if (pack.purchases.length === 0) return empty;
    return (
      <SimpleTable
        headers={[
          t('books.day'),
          t('books.supplier'),
          t('books.goods'),
          t('books.qty'),
          t('books.lineTotal'),
          t('books.vat'),
        ]}
        rows={pack.purchases.map((r) => [
          r.day,
          r.supplier_name,
          r.goods_name,
          String(r.quantity),
          money(r.line_total),
          money(r.invoice_vat),
        ])}
      />
    );
  }
  if (tab === 'stock') {
    if (pack.stock.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.ingredient'), t('books.delta'), t('books.value')]}
        rows={pack.stock.map((r) => [r.day, r.ingredient, String(r.delta), money(r.value)])}
      />
    );
  }
  if (tab === 'waste') {
    if (pack.waste.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.ingredient'), t('books.qty'), t('books.value')]}
        rows={pack.waste.map((r) => [r.day, r.ingredient, String(r.quantity), money(r.value)])}
      />
    );
  }
  if (tab === 'drops') {
    if (pack.drops.length === 0) return empty;
    return (
      <SimpleTable
        headers={[t('books.day'), t('books.expected'), t('books.counted'), t('books.status')]}
        rows={pack.drops.map((r) => [
          r.day,
          money(r.expected_amount),
          r.counted_amount == null ? '' : money(r.counted_amount),
          r.status,
        ])}
      />
    );
  }
  if (pack.expenses.length === 0) return empty;
  return (
    <SimpleTable
      headers={[t('books.day'), t('books.category'), t('books.amount'), t('books.description')]}
      rows={pack.expenses.map((r) => [r.day, r.category, money(r.amount), r.description ?? ''])}
    />
  );
}

function SimpleTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <table className="table table-cards">
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} data-label={headers[j]} className={j === 0 ? 'table-cards-title' : undefined}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
